import { AIText } from "@/components/AIText";
import { useAIAvailability } from "@/lib/aiAvailability";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { useAuth } from "@/context/AuthContext";
import { brl, num, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Check, X, Ship, Package, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const GROUPS = [
  { key: "CRITICO", label: "🔴 Comprar Agora", color: "text-rose-300" },
  { key: "ATENCAO", label: "🟠 Comprar em Breve", color: "text-amber-300" },
  { key: "ADEQUADO", label: "🟢 Estoque Adequado", color: "text-emerald-300" },
  { key: "EXCESSO", label: "⚪ Excesso de Estoque", color: "text-slate-300" },
];

export default function Purchases() {
  const { can, canFinance, hasModule } = useAuth();
  const ai = useAIAvailability(hasModule("ia"));
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const genExplain = async () => {
    if (!ai.available || aiBusy) return;
    setAiBusy(true); setAiText(""); setAiOpen(true);
    const list = (items || []).filter((p) => p.metrics?.quantidade_sugerida > 0).map((p) => ({
      codigo: p.codigo_interno, descricao: p.descricao, status: p.metrics.status_reposicao,
      quantidade_atual: p.quantidade_atual, sugerido: p.metrics.quantidade_sugerida,
      cobertura_meses: p.metrics.cobertura_meses, criticidade: p.criticidade,
      origem: p.origem, lead_time_dias: p.lead_time_dias,
    }));
    try { const { data: r } = await api.post("/ai/purchases-explain", { items: list }); setAiText(r.text); }
    catch (error) {
      if (error?.response?.data?.detail?.code === "AI_TEMPORARILY_UNAVAILABLE") {
        ai.markUnavailable(); setAiOpen(false); return;
      }
      setAiText(error?.response?.data?.detail?.message || "Não foi possível gerar a explicação agora. Tente novamente."); }
    finally { setAiBusy(false); }
  };

  const load = () => api.get("/purchases/recommended").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (part_id, status) => {
    try { await api.post("/purchases/decision", { part_id, status }); toast.success(status === "APROVADO" ? "Sugestão aprovada." : "Sugestão rejeitada."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (items === null) return <div className="space-y-4"><Skeleton className="h-8 w-64 bg-slate-800" /><Skeleton className="h-96 bg-slate-800" /></div>;

  const grouped = GROUPS.map((g) => ({ ...g, list: items.filter((i) => i.metrics.status_reposicao === g.key) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50">Compras Recomendadas</h1><p className="text-slate-400 text-sm">Motor inteligente de reposição — recomendações explicadas. Nenhuma compra é feita automaticamente.</p></div>
        {hasModule("ia") && (
          <Button data-testid="btn-ai-explain" onClick={genExplain} disabled={!ai.available || aiBusy} className="bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white">
            <Sparkles className="h-4 w-4 mr-1" /> {aiBusy ? "Gerando..." : "Explicar com IA"}
          </Button>
        )}
      </div>

      {hasModule("ia") && !ai.available && <p role="status" data-testid="ai-unavailable" className="text-sm text-amber-200">{ai.message}</p>}

      {grouped.map((g) => g.list.length > 0 && (
        <div key={g.key}>
          <h2 className={`font-display text-xl font-semibold mb-3 ${g.color}`}>{g.label} <span className="text-slate-500 text-base">({g.list.length})</span></h2>
          <div className="space-y-3">
            {g.list.map((p) => (
              <Card key={p.id} data-testid={`purchase-row-${p.id}`} className="card-metal p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="h-14 w-14 rounded bg-slate-950 overflow-hidden shrink-0 cursor-pointer" onClick={() => navigate(`/pecas/${p.id}`)}>
                    {p.foto_principal ? <ProtectedImage src={p.foto_principal} alt="" className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><Package className="h-5 w-5 text-slate-700" /></div>}
                  </div>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/pecas/${p.id}`)}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-100">{p.descricao}</span>
                      {p.origem === "IMPORTADA" && <Ship className="h-4 w-4 text-rose-400" />}
                      {p.decision !== "PENDENTE" && <span className={`text-xs rounded px-2 py-0.5 ${p.decision === "APROVADO" ? "bg-emerald-950/60 text-emerald-300" : "bg-rose-950/60 text-rose-300"}`}>{p.decision}</span>}
                    </div>
                    <div className="font-mono text-xs text-sky-400">{p.codigo_interno} · {p.fornecedor?.nome || "—"}</div>
                  </div>
                  <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 text-center text-xs">
                    <div><div className="text-slate-500">Estoque</div><div className="font-mono text-slate-200 text-sm">{num(p.quantidade_atual)}</div></div>
                    <div><div className="text-slate-500">Consumo/mês</div><div className="font-mono text-slate-200 text-sm">{num(p.metrics.consumo_medio_mensal, 1)}</div></div>
                    <div><div className="text-slate-500">Cobertura</div><div className="font-mono text-slate-200 text-sm">{p.metrics.cobertura_meses != null ? `${num(p.metrics.cobertura_meses, 1)}m` : "—"}</div></div>
                    <div><div className="text-slate-500">Sugerido</div><div className="font-mono text-sky-300 text-sm font-bold">{p.metrics.quantidade_sugerida}</div></div>
                    {canFinance && <div><div className="text-slate-500">Custo est.</div><div className="font-mono text-emerald-300 text-sm">{brl(p.metrics.custo_estimado)}</div></div>}
                  </div>
                  {can("COMPRAS") && p.metrics.quantidade_sugerida > 0 && (
                    <div className="flex gap-2">
                      <Button data-testid={`btn-approve-${p.id}`} size="sm" onClick={() => decide(p.id, "APROVADO")} className="bg-emerald-700 hover:bg-emerald-600"><Check className="h-4 w-4" /></Button>
                      <Button data-testid={`btn-reject-${p.id}`} size="sm" variant="secondary" onClick={() => decide(p.id, "REJEITADO")}><X className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
                <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} className="mt-3 flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300">
                  {expanded[p.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Por que esta recomendação?
                </button>
                {expanded[p.id] && (
                  <div className="mt-2 rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-sm space-y-1">
                    <p className="text-slate-200"><b>Recomendação:</b> {p.explanation.recomendacao}</p>
                    <p className="text-slate-400"><b>Motivo:</b> {p.explanation.motivo}</p>
                    <p className="text-slate-500 text-xs">Lead time {p.lead_time_dias} dias · Data recomendada de pedido: {fmtDate(p.data_recomendada_pedido)}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      ))}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-6 w-6 text-sky-400" /> Recomendações explicadas (IA)</DialogTitle></DialogHeader>
          <div data-testid="ai-explain-text" className="text-sm text-slate-200 leading-relaxed min-h-[80px]">{aiBusy && !aiText ? "Gerando explicação..." : <AIText text={aiText} />}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}